import { memo, ReactNode } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface AccessibleSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string; // REQUIRED: For accessibility (will be hidden visually)
  description?: string; // Optional description for screen readers
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
  showClose?: boolean;
  preventOutsideClick?: boolean;
}

/**
 * AccessibleSheet: For modals with custom headers that still need DialogTitle for accessibility.
 *
 * Use this when you want full control over your header styling but need proper a11y.
 * The title prop is hidden visually (for screen readers only) while you render a custom header.
 *
 * Example:
 * ```tsx
 * <AccessibleSheet isOpen={isOpen} onClose={onClose} title="Edit Pronunciation">
 *   <div className="flex justify-between items-center p-8 border-b">
 *     <h2 className="text-xl font-bold">Pronunciations - {genie.name}</h2>
 *     <button onClick={onClose}>×</button>
 *   </div>
 *   { Content }
 * </AccessibleSheet>
 * ```
 */
export const AccessibleSheet = memo(
  ({
    isOpen,
    onClose,
    title,
    description,
    children,
    side = "right",
    className,
    showClose = false,
    preventOutsideClick = false,
  }: AccessibleSheetProps) => {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side={side}
          showClose={showClose}
          className={cn("flex flex-col overflow-hidden p-0", className)}
          onPointerDownOutside={(e) => {
            if (preventOutsideClick) {
              e.preventDefault();
            }
          }}
        >
          {/* ACCESSIBILITY: Both DialogTitle and DialogDescription required by Radix UI */}
          <VisuallyHidden>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description || title}</SheetDescription>
          </VisuallyHidden>
          {children}
        </SheetContent>
      </Sheet>
    );
  },
);

AccessibleSheet.displayName = "AccessibleSheet";
